<script lang="ts">
  import { run } from "svelte/legacy";

  import { onMount } from "svelte";
  import AnimatedBox from "../components/atoms/AnimatedBox.svelte";
  import Campaign from "../components/molecules/Campaign.svelte";
  import ScreenBox from "../components/organisms/ScreenBox.svelte";
  import QueryClientDecorator from "../helpers/QueryClientDecorator.svelte";
  import InitialScreen from "../screens/sharedScreens/InitialScreen.svelte";
  import { popStore, track } from "../store/actions";
  import { initState } from "../store/purchaseflow";
  import DefaultPurchaseFlow from "./DefaultPurchaseFlow.svelte";
  import MovingPurchaseFlow from "./MovingPurchaseFlow.svelte";
  import Confirmation from "../components/organisms/Confirmation.svelte";
  import CustomContent from "../components/molecules/CustomContent.svelte";
  import CampaignText from "../components/atoms/CampaignText.svelte";

  interface Props {
    campaign: string;
    content: string | null;
    confirmationContent: string | null;
    featureImage: string;
    footerContent: string;
    seller: string;
    headerContent: string | null;
    microCampaignUrl: string | undefined;
    errorBanner: string | undefined;
  }

  let {
    campaign,
    content = $bindable(),
    confirmationContent = $bindable(),
    featureImage,
    footerContent,
    seller,
    headerContent = $bindable(),
    microCampaignUrl,
    errorBanner,
  }: Props = $props();

  onMount(() => {
    track(
      initState,
      {
        type: "xstate.init",
      },
      {
        state: {
          value: "init",
        },
      }
    );
  });

  let showConfirmation = $state(false);
  let usingMovingParam = $state(false);

  const { selectedProductId } = popStore();

  const htmlDecode = (input: string) => {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
  };

  const url = new URL(window.location.href);
  let location = $state(window.location.href);

  const setUrls = (isMoving: boolean | undefined) => {
    if (isMoving === true) {
      window.history.pushState(null, "", `${location}#moving`);
    } else if (isMoving === false) {
      window.history.pushState(null, "", `${location}#`);
    } else {
      window.history.pushState(null, "", `${location}`);
    }
  };

  if (content) content = htmlDecode(content);
  if (confirmationContent) confirmationContent = htmlDecode(confirmationContent);
  if (headerContent) headerContent = htmlDecode(headerContent);

  const confirmationBtn = $state(document.querySelector("#showConfirmation"));
  if (confirmationBtn !== null) {
    confirmationBtn.addEventListener("click", () => {
      showConfirmation = !showConfirmation;
    });
  }

  $effect(() => {
    if (showConfirmation && confirmationBtn !== null) {
      confirmationBtn.innerHTML = "Dölj tacksida";
    } else if (confirmationBtn !== null) {
      confirmationBtn.innerHTML = "Visa tacksida";
    }
  });

  let isMovingFlow = $state();
  const movingParam = url.searchParams.get("moving");
  if (movingParam) {
    usingMovingParam = true;
    const movingValue = movingParam.split("/")[0];
    url.searchParams.delete("moving");
    isMovingFlow = movingValue === "true";
  } else {
    isMovingFlow = undefined;
  }

  if (url.searchParams.get("forbrukning") && url.searchParams.get("postnr")) {
    isMovingFlow = false;
  }

  const isQueryParams = (): string => {
    if (url.searchParams && url.searchParams.entries().next().done === false) {
      return "?";
    } else {
      return "";
    }
  };

  location = url.origin + url.pathname + isQueryParams() + url.searchParams.toString();

  const setIsMovingFlow = (value: boolean | undefined) => {
    isMovingFlow = value;
    setUrls(value);
  };

  // svelte-ignore state_referenced_locally
  window.history.pushState(null, "", `${location}`);
</script>

<QueryClientDecorator>
  {#if seller}
    <Campaign>Du skriver detta elavtal som säljare <b>{seller}</b></Campaign>
  {:else if campaign != ""}
    <Campaign><CampaignText /></Campaign>
  {/if}
  <ScreenBox {campaign} {errorBanner} image={featureImage}>
    {#if !showConfirmation}
      {#if isMovingFlow === undefined}
        <AnimatedBox>
          <InitialScreen
            {microCampaignUrl}
            headerContent={headerContent || ""}
            {setIsMovingFlow}
            {campaign}
            content={content || ""}
          />
        </AnimatedBox>
      {:else if isMovingFlow === false}
        <AnimatedBox>
          <DefaultPurchaseFlow
            forceShowEstimation={url.searchParams.get("forbrukning") &&
            url.searchParams.get("postnr")
              ? true
              : false}
            {usingMovingParam}
            {location}
            {campaign}
            {selectedProductId}
            goToStartScreen={setIsMovingFlow}
            {seller}
            confirmationContent={confirmationContent || ""}
          />
        </AnimatedBox>
      {:else if isMovingFlow === true}
        <AnimatedBox>
          <MovingPurchaseFlow
            {location}
            {campaign}
            {selectedProductId}
            {seller}
            goToStartScreen={setIsMovingFlow}
            confirmationContent={confirmationContent || ""}
            {usingMovingParam}
          />
        </AnimatedBox>
      {/if}
    {:else}
      <AnimatedBox>
        <Confirmation
          confirmationType="purchaseFlow"
          confirmationContent={confirmationContent || ""}
        />
      </AnimatedBox>
    {/if}
  </ScreenBox>
  {#if isMovingFlow === undefined && footerContent}
    <CustomContent content={footerContent} />
  {/if}
</QueryClientDecorator>

<style global lang="postcss">
  @tailwind base;
  @tailwind components;
  @tailwind utilities;
</style>
